import { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { useTourContext } from 'contexts/TourContext';

const HelpButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { allTours, pendingCount, startTour } = useTourContext();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (allTours.length === 0) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:text-text-primary hover:bg-hover transition-colors"
        aria-label="Product tours"
      >
        <Icon name="CircleHelp" size={18} />
        {pendingCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-surface border border-border rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold text-text-primary">Page Tours</p>
            <p className="text-xs text-text-secondary mt-0.5">Interactive guides for this page</p>
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y divide-border">
            {allTours.map(tour => {
              const isCompleted = tour.progress?.completed && tour.progress.seen_version >= tour.version;
              return (
                <li key={tour.id}>
                  <button
                    onClick={() => {
                      startTour(tour.id);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-hover text-left transition-colors"
                  >
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                        isCompleted ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'
                      }`}
                    >
                      <Icon name={isCompleted ? 'CheckCircle' : 'PlayCircle'} size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{tour.name}</p>
                      {tour.description && (
                        <p className="text-xs text-text-secondary truncate">{tour.description}</p>
                      )}
                    </div>
                    <Icon name="ChevronRight" size={14} className="flex-shrink-0 text-text-secondary ml-auto" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HelpButton;
